import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAddPatient } from "../../contexts/AddPatientContext";
import { useBookAppointment } from "../../contexts/BookAppointmentContext";
import { useRole } from "../../hooks/useRole";

export interface ArrivalCardData {
  patient_name: string;
  abha_id: string;
  age?: number;
  gender?: string;
  matched: boolean;
  patient_id: string | null;
  last_visit_at?: string | null;
  demographics?: {
    name?: string;
    dob?: string | null;
    gender?: string | null;
  };
}

const AUTO_DISMISS_MS = 60_000;

function fmtLastVisit(iso: string): string {
  const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  const weeks = Math.floor(diffSec / 604800);
  if (weeks < 1) return `${Math.floor(diffSec / 86400)}d ago`;
  return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
}

interface ScanArrivalCardProps {
  card: ArrivalCardData;
  onDismiss: () => void;
}

function ScanArrivalCard({ card, onDismiss }: ScanArrivalCardProps) {
  const navigate = useNavigate();
  const { openAddPatientModal } = useAddPatient();
  const { openBookAppointmentModal } = useBookAppointment();
  const { canOr, isAdmin, isDoctor } = useRole();
  const canCreatePatient = canOr("patients.create", true);
  const canBookAppointment = canOr("appointments.create", true);
  const canViewAppointments = canOr("appointments.view", true);
  // Start Visit lands on the patient profile (its Start Visit CTA runs the
  // credential checks), so it needs both the profile and the clinical grant.
  const canStartVisit =
    canOr("patients.view", true) && canOr("visits.write", isAdmin || isDoctor);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    timerRef.current = setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [onDismiss]);

  const meta = [card.age != null ? `${card.age}y` : null, card.gender]
    .filter(Boolean)
    .join(" · ");

  // Both used to target /patients/:id/appointments/new and /visits/new, which
  // are not routes — the catch-all bounced the user to /login.
  const handleBookAppointment = () => {
    onDismiss();
    // Same deep link the patient profile uses, which prefills the patient. A
    // booking-only role cannot open /appointments, so give them the bare modal.
    if (canViewAppointments) {
      navigate(`/appointments?open_booking=true&patient_id=${card.patient_id}`);
    } else {
      openBookAppointmentModal();
    }
  };

  const handleStartVisit = () => {
    onDismiss();
    navigate(`/patients/${card.patient_id}`);
  };

  const handleRegisterPatient = () => {
    onDismiss();
    openAddPatientModal(card.demographics ?? {});
  };

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderLeft: `4px solid ${card.matched ? "#16a34a" : "#2563eb"}`,
        borderRadius: 12,
        boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
        padding: "16px 20px",
        width: 320,
        position: "relative",
      }}
    >
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          position: "absolute",
          top: 10,
          right: 12,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#94a3b8",
          fontSize: 20,
          lineHeight: 1,
          padding: 0,
        }}
      >
        ×
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 16 }}>📱</span>
        <span
          style={{
            background: card.matched ? "#dcfce7" : "#dbeafe",
            color: card.matched ? "#16a34a" : "#2563eb",
            borderRadius: 6,
            padding: "2px 8px",
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {card.matched ? "Returning patient" : "New patient"}
        </span>
        <span style={{ fontSize: 11, color: "#94a3b8" }}>ABHA scan</span>
      </div>

      <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a", marginBottom: 2 }}>
        {card.patient_name}
      </div>
      {meta && <div style={{ fontSize: 13, color: "#64748b", marginBottom: 2 }}>{meta}</div>}
      <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: card.last_visit_at ? 6 : 12 }}>
        ABHA: {card.abha_id}
      </div>
      {card.last_visit_at && (
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
          Last visit: {fmtLastVisit(card.last_visit_at)}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {card.matched ? (
          <>
            {canBookAppointment && (
              <button
                onClick={handleBookAppointment}
                style={{
                  flex: 1,
                  padding: "7px 0",
                  background: "#0f172a",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Book Appointment
              </button>
            )}
            {canStartVisit && (
              <button
                onClick={handleStartVisit}
                style={{
                  flex: 1,
                  padding: "7px 0",
                  background: "#f1f5f9",
                  color: "#0f172a",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Start Visit
              </button>
            )}
          </>
        ) : canCreatePatient ? (
          <button
            onClick={handleRegisterPatient}
            style={{
              width: "100%",
              padding: "8px 0",
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Register Patient →
          </button>
        ) : (
          // Unmatched ABDM scan and no patients.create grant: say why rather than
          // offering a register button whose POST would 403.
          <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>
            Not registered — you do not have permission to register patients.
          </p>
        )}
      </div>
    </div>
  );
}

interface ScanArrivalCardStackProps {
  cards: ArrivalCardData[];
  onDismiss: (idx: number) => void;
}

export function ScanArrivalCardStack({ cards, onDismiss }: ScanArrivalCardStackProps) {
  if (cards.length === 0) return null;
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        maxHeight: "80vh",
        overflowY: "auto",
      }}
    >
      {cards.map((card, idx) => (
        <ScanArrivalCard key={idx} card={card} onDismiss={() => onDismiss(idx)} />
      ))}
    </div>
  );
}
